/**
 * Espejo DISPLAY-ONLY de los requisitos-por-calculadora, para pintar la matriz
 * de cross-sell (solo lectura) en el editor de planes.
 *
 * ⚠️ La fuente de verdad de COMPORTAMIENTO vive en flow:
 * flow/src/lib/suggested-plans-compat.ts. Este archivo NO es una copia literal:
 * su REQUISITOS_BASE codifica "base(tipo) + un ejemplo representativo de
 * atributos + rut" (flow deja precio_base_atributos/construccion_comuna vacíos
 * y deriva sus atributos por-plan en runtime). Al agregar/cambiar una
 * calculadora, mantén CONSISTENTE el resultado de la matriz §4.2 — NO copies
 * los arrays de flow tal cual. Ver spec:
 * docs/superpowers/specs/2026-07-21-matriz-cross-sell-planes-design.md
 */

export const CALCULATOR_TYPES = [
  'fijo',
  'por_mascota',
  'tramo_edad',
  'monto_tasa',
  'precio_base_atributos',
  'construccion_comuna',
];

export const LABEL_CALCULADORA = {
  fijo: 'Fijo',
  por_mascota: 'Mascota',
  tramo_edad: 'Tramo edad (salud)',
  monto_tasa: 'Monto × tasa (vida)',
  precio_base_atributos: 'Atributos (RC médica)',
  construccion_comuna: 'Construcción + comuna (incendio)',
};

/** Requisitos base para cotizar exacto un plan de cada tipo (tokens de dato). */
export const REQUISITOS_BASE = {
  fijo: [],
  por_mascota: [],
  tramo_edad: ['fechaNacimiento', 'sexo', 'prevision'],
  monto_tasa: ['montoAsegurado'],
  precio_base_atributos: ['atributo:especialidad'],
  construccion_comuna: ['atributo:comuna', 'atributo:tipo_construccion', 'rut'],
};

/** Datos que aporta un flujo cuyo plan base usa cada calculadora. */
export const DATOS_QUE_APORTA_FLUJO = {
  fijo: [],
  por_mascota: [],
  tramo_edad: ['fechaNacimiento', 'sexo', 'prevision'],
  monto_tasa: ['montoAsegurado'],
  precio_base_atributos: ['atributo:especialidad'],
  construccion_comuna: ['atributo:comuna', 'atributo:tipo_construccion', 'rut'],
};

/** ¿Un plan de calculadora `destino` se sugiere en un flujo de calculadora `origen`? */
export function esCompatible(origen, destino) {
  // Fail-closed: un tipo destino no reconocido no se sugiere (coincide con flow,
  // que en esCotizableExacto devuelve false para tipos fuera de REQUISITOS_BASE).
  if (!(destino in REQUISITOS_BASE)) return false;
  const req = REQUISITOS_BASE[destino];
  const disp = new Set(DATOS_QUE_APORTA_FLUJO[origen] || []);
  return req.every((r) => disp.has(r));
}

/** Grid completo origen×destino para la vista de referencia. */
export function matrizCrossSell() {
  return CALCULATOR_TYPES.map((origen) => ({
    origen,
    celdas: CALCULATOR_TYPES.map((destino) => ({
      destino,
      compatible: esCompatible(origen, destino),
    })),
  }));
}

/** Flujos (orígenes) donde un plan de este tipo se puede sugerir. */
export function flujosDondeSeSugiere(destino) {
  return CALCULATOR_TYPES.filter((origen) => esCompatible(origen, destino));
}

/** Tipos de plan que se pueden sugerir dentro del flujo de este tipo. */
export function sugeridosEnFlujo(origen) {
  return CALCULATOR_TYPES.filter((destino) => esCompatible(origen, destino));
}
